<template>
  <div class="aniversariante-preview-modal">
    <modal
      elem-id="aniversariante-preview"
      :show="isVisible"
      :title="t('Enviar Mensagem de Aniversário')"
      size="medium"
      :close-label="t('Cancelar')"
      :save-label="t('Enviar WhatsApp')"
      save-icon="pi pi-whatsapp"
      :save-fn="handleSend"
      :save-disabled-fn="() => isSending"
      @close="handleClose"
      @dismiss="handleClose"
    >
      <div v-if="aniversariante" class="preview-content">
        <div class="cliente-info">
          <h3>{{ aniversariante.cliente_nome }}</h3>
          <p class="info-row">
            <strong>{{ t('Telefone:') }}</strong>
            {{ formatPhone(aniversariante) }}
          </p>
          <p class="info-row">
            <strong>{{ t('Aniversário:') }}</strong>
            {{ formatDate(aniversariante.cliente_data_nascimento) }}
            <span class="badge">({{ aniversariante.idade }})</span>
          </p>
        </div>

        <div class="message-section">
          <label>{{ t('Mensagem:') }}</label>
          <input-rich-text
            v-model="mensagemEditada"
            data-testid="aniversariantes-preview-message"
            class="message-textarea"
          />
        </div>

        <div class="placeholders-info">
          <small>{{ t('Placeholders disponíveis:') }}</small>
          <div class="placeholders-list">
            <code>{nome}</code>,
            <code>{primeiro_nome}</code>,
            <code>{idade}</code>,
            <code>{dia}</code>,
            <code>{mes}</code>,
            <code>{franquia}</code>,
            <code>{desconto}</code>
          </div>
        </div>
      </div>
    </modal>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAniversariantes } from '@/composables/useAniversariantes';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';
import { storageService } from '@/services/storageService';

type Id = number | string;

interface Aniversariante {
  cliente_id: Id;
  cliente_nome?: string;
  cliente_ddi?: string;
  cliente_ddd?: string;
  cliente_telefone?: string;
  cliente_data_nascimento?: string;
  idade?: number | string;
  [key: string]: unknown;
}

interface Franquia {
  nome: string;
  [key: string]: unknown;
}

interface OpenPayload {
  aniversariante: Aniversariante;
  franquiaId: Id;
  franquia?: Franquia;
  mensagemConfig?: string;
}

const { t } = useI18n();
const router = useRouter();
const { substituirPlaceholders, marcarEnviado } = useAniversariantes();

const isVisible = ref<boolean>(false);
const aniversariante = ref<Aniversariante | null>(null);
const franquiaId = ref<Id | null>(null);
const franquia = ref<Franquia | null>(null);
const mensagemConfig = ref<string>('');
const mensagemEditada = ref<string>('');
const isSending = ref<boolean>(false);

eventBus.on<OpenPayload>('modal.aniversariante-preview.open', (data?: OpenPayload) => {
  if (!data) return;
  log('AniversariantePreviewModal', 'eventBus', 'Opened with data', data);
  aniversariante.value = data.aniversariante;
  franquiaId.value = data.franquiaId;
  franquia.value = data.franquia || { nome: '' };
  mensagemConfig.value = data.mensagemConfig || t('Olá, {nome}! 🎉 Feliz aniversário!');

  mensagemEditada.value = substituirPlaceholders(
    mensagemConfig.value,
    data.aniversariante,
    data.franquia || { nome: '' }
  );

  isVisible.value = true;
});

const formatPhone = (cliente: Aniversariante): string => {
  const { cliente_ddi, cliente_ddd, cliente_telefone } = cliente;
  if (!cliente_telefone) return '-';
  return `${cliente_ddi || ''} (${cliente_ddd || ''}) ${cliente_telefone}`;
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR');
};

const handleClose = (): void => {
  isVisible.value = false;
  aniversariante.value = null;
  mensagemEditada.value = '';
};

const persistPendingChat = (chatData: Record<string, unknown>): void => {
  storageService.session.setJSON('whatsapp_pending_chat', chatData);
};

const handleSend = async (): Promise<void> => {
  log('AniversariantePreviewModal', 'handleSend', 'started');

  if (!aniversariante.value) return;

  isSending.value = true;

  try {
    const phone = buildPhoneNumber(aniversariante.value);
    log('AniversariantePreviewModal', 'handleSend', 'phone built', phone);

    const chatData = {
      phone,
      deviceId: null,
      prefillMessage: mensagemEditada.value
    };
    persistPendingChat(chatData);

    await marcarEnviado(
      Number(aniversariante.value.cliente_id),
      Number(franquiaId.value),
      mensagemEditada.value
    );

    eventBus.emit('aniversariantes.refresh');

    handleClose();

    await router.push('/messaging-inbox');
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    log('AniversariantePreviewModal', 'handleSend', 'Error', message);
  } finally {
    isSending.value = false;
  }
};

const buildPhoneNumber = (cliente: Aniversariante): string => {
  const { cliente_ddi, cliente_ddd, cliente_telefone } = cliente;

  const ddi = (cliente_ddi || '').replace(/\D/g, '');
  const ddd = (cliente_ddd || '').replace(/\D/g, '');
  const telefone = (cliente_telefone || '').replace(/\D/g, '');

  log('AniversariantePreviewModal', 'buildPhoneNumber', 'parts', { ddi, ddd, telefone });

  let phone = '';
  if (ddi && ddd && telefone) {
    phone = `+${ddi}${ddd}${telefone}`;
  } else if (ddd && telefone) {
    phone = `+55${ddd}${telefone}`;
  } else {
    log('AniversariantePreviewModal', 'buildPhoneNumber', 'incomplete phone data');
  }

  return phone;
};
</script>

<style scoped>
.preview-content {
  padding: 10px 0;
}

.cliente-info {
  background: var(--surface-100);
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
}

.cliente-info h3 {
  margin: 0 0 10px 0;
  font-size: 18px;
}

.info-row {
  margin: 5px 0;
  font-size: 14px;
}

.badge {
  display: inline-block;
  background: var(--primary-50);
  color: var(--primary-600);
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  margin-left: 5px;
}

.message-section {
  margin-bottom: 15px;
}

.message-section label {
  display: block;
  font-weight: 500;
  margin-bottom: 8px;
}

.message-textarea {
  width: 100%;
  font-family: monospace;
}

.placeholders-info {
  background: var(--surface-50);
  border-radius: 6px;
  padding: 10px;
  font-size: 12px;
}

.placeholders-info small {
  display: block;
  margin-bottom: 5px;
  color: var(--text-color-secondary);
}

.placeholders-list {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.placeholders-list code {
  background: var(--surface-200);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 11px;
}
</style>
